import { MACRS_CLASSES, isDepreciationMethod, type DepreciationMethod } from './model';

/**
 * Depreciation schedules for the equipment register.
 *
 * Book methods (straight-line, double-declining, sum-of-years' digits)
 * run in asset years from the in-service month and are spread evenly
 * across months (full-month convention), then rolled up into calendar
 * years. A mid-year purchase therefore gets a partial first and last
 * calendar year.
 *
 * MACRS uses the IRS GDS half-year tables, one table row per calendar
 * (tax) year starting with the year placed in service. Salvage is
 * ignored, as the tables require.
 *
 * Disposal stops depreciation: book methods stop the month before the
 * disposal month; MACRS takes half of the disposal year's amount and
 * nothing when the asset is placed in service and disposed in the same
 * year.
 *
 * Everything is in cents. Calendar-year rows are rounded on the running
 * total so the rows always sum exactly to the depreciable basis.
 */

/** GDS half-year convention percentages (IRS Pub. 946, Table A-1). */
const MACRS_HALF_YEAR_RATES: Record<number, number[]> = {
  3: [33.33, 44.45, 14.81, 7.41],
  5: [20.0, 32.0, 19.2, 11.52, 11.52, 5.76],
  7: [14.29, 24.49, 17.49, 12.49, 8.93, 8.92, 8.93, 4.46],
  10: [10.0, 18.0, 14.4, 11.52, 9.22, 7.37, 6.55, 6.55, 6.56, 6.55, 3.28],
  15: [5.0, 9.5, 8.55, 7.7, 6.93, 6.23, 5.9, 5.9, 5.91, 5.9, 5.91, 5.9, 5.91, 5.9, 5.91, 2.95],
  20: [
    3.75, 7.219, 6.677, 6.177, 5.713, 5.285, 4.888, 4.522, 4.462, 4.461, 4.462, 4.461, 4.462,
    4.461, 4.462, 4.461, 4.462, 4.461, 4.462, 4.461, 2.231,
  ],
};

export interface DepreciationInputs {
  costCents: number | null;
  salvageCents: number | null;
  method: string;
  usefulLifeYears: number | null;
  /** Depreciation start (`YYYY-MM-DD`): in-service date, else purchase date. */
  startOn: string | null;
  disposedOn?: string | null;
}

export interface DepreciationYear {
  year: number;
  /** Months of depreciation that fall in this calendar year. */
  months: number;
  beginningCents: number;
  depreciationCents: number;
  accumulatedCents: number;
  endingCents: number;
}

interface MonthKey {
  year: number;
  /** 0-11 */
  month: number;
}

export type DepreciationSchedule =
  | { kind: 'incomplete'; missing: string[] }
  | { kind: 'not_depreciated'; costCents: number }
  | {
      kind: 'schedule';
      method: Exclude<DepreciationMethod, 'none'>;
      costCents: number;
      /** Salvage actually applied (always 0 for MACRS / expensed). */
      salvageCents: number;
      basisCents: number;
      rows: DepreciationYear[];
      start: MonthKey;
      /** Depreciation per month in cents (unrounded), from `start`. */
      monthly: number[];
      /** Last month carrying depreciation, or null when there is none. */
      lastMonth: MonthKey | null;
      disposedOn: string | null;
    };

export function buildDepreciationSchedule(inputs: DepreciationInputs): DepreciationSchedule {
  const method: DepreciationMethod = isDepreciationMethod(inputs.method)
    ? inputs.method
    : 'straight_line';
  const missing: string[] = [];
  if (inputs.costCents == null) missing.push('purchase price');

  if (method === 'none') {
    if (missing.length) return { kind: 'incomplete', missing };
    return { kind: 'not_depreciated', costCents: inputs.costCents ?? 0 };
  }

  const start = parseMonth(inputs.startOn);
  if (!start) missing.push('in-service or purchase date');

  const life = inputs.usefulLifeYears;
  if (method === 'macrs') {
    if (life == null || !(MACRS_CLASSES as readonly number[]).includes(life)) {
      missing.push('MACRS recovery period (3, 5, 7, 10, 15 or 20 years)');
    }
  } else if (method !== 'expensed' && (life == null || life < 1)) {
    missing.push('useful life');
  }

  if (missing.length || !start || inputs.costCents == null) {
    return { kind: 'incomplete', missing };
  }

  const cost = inputs.costCents;
  const salvage =
    method === 'macrs' || method === 'expensed' ? 0 : Math.min(inputs.salvageCents ?? 0, cost);

  let monthly: number[];
  if (method === 'expensed') {
    monthly = [cost];
  } else if (method === 'macrs') {
    monthly = macrsMonthly(cost, life!, start.month);
  } else {
    monthly = bookMonthly(method, cost, salvage, life!);
  }

  const disposed = parseMonth(inputs.disposedOn ?? null);
  if (disposed) {
    monthly = truncateForDisposal(monthly, method, start, disposed, life, cost);
  }

  const rows = rollUpByYear(monthly, start, cost);
  let lastMonth: MonthKey | null = null;
  for (let i = monthly.length - 1; i >= 0; i -= 1) {
    if (monthly[i] > 0) {
      lastMonth = addMonths(start, i);
      break;
    }
  }

  return {
    kind: 'schedule',
    method,
    costCents: cost,
    salvageCents: salvage,
    basisCents: cost - salvage,
    rows,
    start,
    monthly,
    lastMonth,
    disposedOn: disposed ? (inputs.disposedOn ?? null) : null,
  };
}

export interface DepreciationPosition {
  accumulatedCents: number;
  bookValueCents: number;
  /** True once the as-of date is on or after the disposal date. */
  disposed: boolean;
}

/**
 * Accumulated depreciation and book value at the end of the month
 * containing `asOf`. Returns null when the schedule can't be computed.
 * A disposed asset carries no book value after its disposal date.
 */
export function depreciationAsOf(
  schedule: DepreciationSchedule,
  asOf: string,
): DepreciationPosition | null {
  if (schedule.kind === 'incomplete') return null;
  if (schedule.kind === 'not_depreciated') {
    return { accumulatedCents: 0, bookValueCents: schedule.costCents, disposed: false };
  }
  const at = parseMonth(asOf);
  if (!at) return null;
  const disposed = !!schedule.disposedOn && asOf.slice(0, 10) >= schedule.disposedOn.slice(0, 10);
  const idx = monthsBetween(schedule.start, at);
  let sum = 0;
  const upTo = disposed ? schedule.monthly.length - 1 : Math.min(idx, schedule.monthly.length - 1);
  for (let i = 0; i <= upTo; i += 1) sum += schedule.monthly[i];
  const accumulatedCents = Math.round(sum);
  return {
    accumulatedCents,
    bookValueCents: disposed ? 0 : schedule.costCents - accumulatedCents,
    disposed,
  };
}

/** Book value on the disposal date, for the gain / loss read-out. */
export function bookValueAtDisposal(schedule: DepreciationSchedule): number | null {
  if (schedule.kind === 'not_depreciated') return schedule.costCents;
  if (schedule.kind !== 'schedule' || !schedule.disposedOn) return null;
  const total = schedule.monthly.reduce((acc, v) => acc + v, 0);
  return schedule.costCents - Math.round(total);
}

export function formatMonthKey(key: MonthKey): string {
  return new Date(key.year, key.month, 1).toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
  });
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

function bookMonthly(
  method: 'straight_line' | 'declining_balance' | 'sum_of_years_digits',
  cost: number,
  salvage: number,
  life: number,
): number[] {
  const basis = Math.max(0, cost - salvage);
  const annual: number[] = [];
  if (method === 'straight_line') {
    for (let i = 0; i < life; i += 1) annual.push(basis / life);
  } else if (method === 'sum_of_years_digits') {
    const digits = (life * (life + 1)) / 2;
    for (let i = 0; i < life; i += 1) annual.push((basis * (life - i)) / digits);
  } else {
    // 200% declining balance, switching to straight-line on the remaining
    // basis once that gives the larger charge. The final year always
    // lands exactly on salvage.
    let book = cost;
    const rate = 2 / life;
    for (let i = 0; i < life; i += 1) {
      const remaining = Math.max(0, book - salvage);
      const declining = book * rate;
      const straight = remaining / (life - i);
      const charge = Math.min(Math.max(declining, straight), remaining);
      annual.push(charge);
      book -= charge;
    }
  }
  const monthly: number[] = [];
  for (const amount of annual) {
    for (let m = 0; m < 12; m += 1) monthly.push(amount / 12);
  }
  return monthly;
}

function macrsMonthly(cost: number, recoveryYears: number, startMonth: number): number[] {
  const rates = MACRS_HALF_YEAR_RATES[recoveryYears];
  const monthly: number[] = [];
  rates.forEach((rate, k) => {
    const amount = (cost * rate) / 100;
    const months = k === 0 ? 12 - startMonth : 12;
    for (let m = 0; m < months; m += 1) monthly.push(amount / months);
  });
  return monthly;
}

function truncateForDisposal(
  monthly: number[],
  method: Exclude<DepreciationMethod, 'none'>,
  start: MonthKey,
  disposed: MonthKey,
  life: number | null,
  cost: number,
): number[] {
  const idx = monthsBetween(start, disposed);
  if (idx < 0) return [];
  if (method === 'expensed') return monthly;

  if (method === 'macrs') {
    const rates = MACRS_HALF_YEAR_RATES[life ?? 0];
    const taxYear = disposed.year - start.year;
    if (!rates || taxYear >= rates.length) return monthly;
    if (taxYear === 0) return [];
    const kept = monthly.slice(0, 12 - start.month + 12 * (taxYear - 1));
    const amount = (cost * rates[taxYear]) / 100 / 2;
    const months = disposed.month + 1;
    for (let m = 0; m < months; m += 1) kept.push(amount / months);
    return kept;
  }

  return monthly.slice(0, Math.min(idx, monthly.length));
}

function rollUpByYear(monthly: number[], start: MonthKey, cost: number): DepreciationYear[] {
  const byYear = new Map<number, { months: number; amount: number }>();
  monthly.forEach((amount, i) => {
    const { year } = addMonths(start, i);
    const bucket = byYear.get(year) ?? { months: 0, amount: 0 };
    bucket.months += 1;
    bucket.amount += amount;
    byYear.set(year, bucket);
  });

  const rows: DepreciationYear[] = [];
  let running = 0;
  let prevRounded = 0;
  for (const year of [...byYear.keys()].sort((a, b) => a - b)) {
    const bucket = byYear.get(year)!;
    running += bucket.amount;
    const rounded = Math.round(running);
    rows.push({
      year,
      months: bucket.months,
      beginningCents: cost - prevRounded,
      depreciationCents: rounded - prevRounded,
      accumulatedCents: rounded,
      endingCents: cost - rounded,
    });
    prevRounded = rounded;
  }
  return rows;
}

function parseMonth(value: string | null | undefined): MonthKey | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})/.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  if (month < 0 || month > 11) return null;
  return { year, month };
}

function monthsBetween(from: MonthKey, to: MonthKey): number {
  return (to.year - from.year) * 12 + (to.month - from.month);
}

function addMonths(key: MonthKey, months: number): MonthKey {
  const total = key.year * 12 + key.month + months;
  return { year: Math.floor(total / 12), month: total % 12 };
}
